import { auth } from "../config/firebase";

// Track last token fetch to prevent excessive calls and race conditions
let lastTokenFetchTime = 0;
let lastTokenValue: string | null = null;
let tokenFetchPromise: Promise<string> | null = null; // Track in-flight requests
const TOKEN_FETCH_COOLDOWN = 5 * 1000; // 5 seconds - don't fetch token more than once per 5 seconds

/**
 * Get authentication token with smart caching and race condition prevention
 * @param forceRefresh - If true, forces token refresh even if cached
 * @returns Promise<string> - The Firebase auth token
 */
export const getAuthToken = async (forceRefresh: boolean = false): Promise<string> => {
  const user: any = auth.currentUser;
  if (!user) {
    // Clear cache if user is null
    lastTokenValue = null;
    lastTokenFetchTime = 0;
    tokenFetchPromise = null;
    throw new Error("User not authenticated");
  }
  
  const now = Date.now();
  
  // If forcing refresh, clear cache and existing promise
  if (forceRefresh) {
    lastTokenValue = null;
    lastTokenFetchTime = 0;
    tokenFetchPromise = null;
  }
  
  // If there's an in-flight request, return that promise to prevent race conditions
  if (tokenFetchPromise && !forceRefresh) {
    return tokenFetchPromise;
  }
  
  // If not forcing refresh and we have a recent token, return cached value
  if (!forceRefresh && lastTokenValue && (now - lastTokenFetchTime) < TOKEN_FETCH_COOLDOWN) {
    return lastTokenValue;
  }
  
  // Create a single promise for this token fetch (prevents multiple simultaneous calls)
  tokenFetchPromise = (async () => {
    try {
      // Get token (with optional force refresh)
      const token = await user.getIdToken(forceRefresh);
      
      // Cache token value and timestamp
      lastTokenValue = token;
      lastTokenFetchTime = now;
      
      return token;
    } catch (error: any) {
      console.error("[Auth] Failed to get auth token:", error);
      
      // Clear cache on error
      lastTokenValue = null;
      lastTokenFetchTime = 0;
      
      // If token is invalid/expired, throw a specific error that can be caught by API calls
      if (
        error?.code === "auth/user-token-expired" ||
        error?.code === "auth/invalid-user-token" ||
        error?.code === "auth/user-disabled" ||
        error?.code === "auth/user-not-found"
      ) {
        const authError = new Error("Authentication token expired or invalid. Please login again.");
        (authError as any).status = 401;
        (authError as any).isUnauthorized = true;
        (authError as any).code = error.code;
        throw authError;
      }
      
      // Re-throw other errors
      throw error;
    } finally {
      // Clear the promise reference after completion
      tokenFetchPromise = null;
    }
  })();
  
  return tokenFetchPromise;
};
